import React, { useEffect } from 'react';
import { Bell, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import {
    markAllNotificationsRead,
    clearAllNotifications,
    deleteNotification,
    setHighlightedOrderId,
    setHighlightedMilestoneId,
} from '../../store/slices/uiSlice';
import { formatRawDateTime } from '../../utils/format';
import { Dropdown } from '../ui/dropdown/Dropdown';
import { DropdownItem } from '../ui/dropdown/DropdownItem';

interface NotificationPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

const formatDate = (iso: string) => formatRawDateTime(iso);

// TailAdmin's NotificationDropdown: an anchored popover under the bell — header, scrollable list.
const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const notifications = useAppSelector((state: RootState) => state.ui.notifications);

    useEffect(() => {
        if (isOpen) dispatch(markAllNotificationsRead());
    }, [isOpen, dispatch]);

    const handleView = (data: Record<string, string> = {}, title = '', body = '') => {
        const orderId = data.order_id || data.orderId || data.orderID || data.order;
        const milestoneId = data.milestone_id || data.milestoneId;
        const recipientMobile = data.recipient_mobile || data.recipientMobile || data.mobile || data.user_mobile;
        const route = data.url || data.route || data.path || data.link;

        if (route && typeof route === 'string') {
            try {
                const parsed = new URL(route, window.location.origin);
                const hlOrder = parsed.searchParams.get('highlight_order');
                const hlMilestone = parsed.searchParams.get('highlight_milestone');
                if (hlOrder) dispatch(setHighlightedOrderId(hlOrder));
                if (hlMilestone) dispatch(setHighlightedMilestoneId(hlMilestone));
                navigate(parsed.pathname + parsed.search);
            } catch {
                navigate(route.startsWith('/') ? route : `/${route}`);
            }
        } else if (data.type === 'MILESTONE_ACHIEVED' || milestoneId || title.toLowerCase().includes('milestone')) {
            if (milestoneId) dispatch(setHighlightedMilestoneId(milestoneId));
            navigate('/offer-settings');
        } else if ((data.type === 'REFERRAL_REWARD' || title.toLowerCase().includes('referral')) && recipientMobile) {
            navigate(`/user-management/network/${recipientMobile}`);
        } else if (orderId) {
            dispatch(setHighlightedOrderId(orderId));
            navigate('/orders');
        } else {
            const text = `${title} ${body}`.toLowerCase();
            if (text.includes('order')) {
                const match = `${title} ${body}`.match(/\b([A-Za-z0-9_-]{8,32})\b/);
                if (match) dispatch(setHighlightedOrderId(match[1]));
                navigate('/orders');
            } else if (text.includes('roaster') || text.includes('procure')) {
                navigate('/roaster');
            } else if (text.includes('kyc') || text.includes('customer') || text.includes('user')) {
                navigate('/user-management');
            } else if (text.includes('coupon')) {
                navigate('/coupons');
            } else if (text.includes('leaderboard') || text.includes('marketing')) {
                navigate('/marketing/leaderboard');
            } else {
                navigate('/orders');
            }
        }
        onClose();
    };

    const handleDeleteSingle = (id: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(deleteNotification(id));
    };

    const handleClearAll = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(clearAllNotifications());
    };

    return (
        <Dropdown
            isOpen={isOpen}
            onClose={onClose}
            className="right-[24px] md:right-0 mt-[17px] flex h-[min(480px,70vh)] w-[calc(100vw-3rem)] max-w-[361px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg z-[200]"
        >
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                    <h5 className="text-lg font-semibold text-gray-800">Notifications</h5>
                    {notifications.length > 0 && (
                        <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full">
                            {notifications.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {notifications.length > 0 && (
                        <button
                            type="button"
                            onClick={handleClearAll}
                            title="Clear all"
                            className="text-gray-400 hover:text-red-600 transition p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer"
                        >
                            <Trash2 size={15} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer"
                    >
                        <X size={17} />
                    </button>
                </div>
            </div>

            <ul className="flex flex-col h-auto overflow-y-auto custom-scrollbar list-none m-0 p-0">
                {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3 py-10">
                        <Bell size={40} className="opacity-25" />
                        <p className="text-sm">No notifications yet</p>
                    </div>
                ) : (
                    notifications.map((notif) => (
                        <li key={notif.id}>
                            <DropdownItem
                                tag="div"
                                onItemClick={() => handleView(notif.data || {}, notif.title, notif.body)}
                                className={`flex flex-col items-start gap-1 rounded-lg border-b border-gray-100 px-3 py-3 hover:bg-gray-100 cursor-pointer ${!notif.read ? 'bg-[var(--color-brand-25,#f2f7ff)]' : ''}`}
                            >
                                <div className="flex items-start gap-2 w-full">
                                    {!notif.read && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0 mt-1.5" />
                                    )}
                                    <span className="text-theme-sm font-semibold text-gray-800 text-left flex-1">
                                        {notif.title}
                                    </span>
                                </div>
                                <p className={`text-theme-xs text-gray-500 text-left break-words w-full ${!notif.read ? 'pl-3.5' : ''}`}>
                                    {notif.body}
                                </p>
                                <div className={`flex items-center justify-between w-full mt-1 ${!notif.read ? 'pl-3.5' : ''}`}>
                                    <span className="text-gray-400 text-theme-xs">
                                        {formatDate(notif.receivedAt)}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDeleteSingle(notif.id, e)}
                                        title="Delete notification"
                                        className="text-gray-400 hover:text-red-500 transition p-1 rounded hover:bg-gray-200/70 cursor-pointer"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </DropdownItem>
                        </li>
                    ))
                )}
            </ul>
        </Dropdown>
    );
};

export default NotificationPanel;
